/**
 * Light / dark theme controller.
 *
 * Default: light cream (Lovable-inspired).
 * Optional: dark — toggles via <html data-theme="dark">.
 *
 * Persistence: localStorage key "canvaloop-theme".
 * Initial value resolution order: explicit override → localStorage → "light".
 */

import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "canvaloop-theme";

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch { return null; }
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => readStored() ?? "light");

  // Apply on mount + whenever it changes
  useEffect(() => { applyTheme(theme); }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  return { theme, setTheme, toggle };
}

/** Force a theme for a single subtree (e.g. dark canvas in Editor).
 *  Restores the previous theme on unmount. */
export function useForceTheme(forced: Theme): void {
  useEffect(() => {
    const prev = (document.documentElement.getAttribute("data-theme") as Theme) ?? "light";
    applyTheme(forced);
    return () => { applyTheme(prev); };
  }, [forced]);
}
